// PatchManagement.js
import React, { useState, useEffect } from "react";
import {
  Container, Card, CardBody, Spinner, Alert, Button
} from "reactstrap";
import FeatherIcon from "feather-icons-react";
import patchService from "../../services/PatchService";
import PatchAgentsTable from "./PatchAgentsTable";
import ScheduledPatchesCard from "./ScheduledPatchesCard";

const PatchManagement = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingPatchData, setFetchingPatchData] = useState(false);
  const [error, setError] = useState(null);
  const [scheduledJobs, setScheduledJobs] = useState([]);

  useEffect(() => {
    document.title = "Patch Management - RiskNoX";
    initializePatchManagement();
    
    const interval = setInterval(() => {
      refreshAgentData();
    }, 300000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const initializePatchManagement = async () => {
    try {
      setLoading(true);
      setFetchingPatchData(true);
      console.log('Initializing patch management...');
      
      const response = await patchService.getPatchInfo();
      const agentList = response.agents || [];
      setAgents(agentList);

      await loadScheduledJobs();

      setLoading(false);

      const onlineAgents = agentList.filter(agent => agent.status === 'online');
      
      if (onlineAgents.length === 0) {
        setError('No online agents found');
        setFetchingPatchData(false);
        return;
      }

      console.log(`Requesting patch data from ${onlineAgents.length} online agents...`);
      
      const agentIds = onlineAgents.map(a => a.agent_id);
      
      try {
        await patchService.requestPatchInfo(agentIds);
        
        setError(`Fetching patch data from ${onlineAgents.length} agent(s). This may take 30-60 seconds...`);
        
        let pollCount = 0;
        const maxPolls = 12;
        
        const pollInterval = setInterval(async () => {
          pollCount++;
          
          try {
            const updatedResponse = await patchService.getPatchInfo();
            setAgents(updatedResponse.agents || []);
            
            const agentsWithData = updatedResponse.agents.filter(agent => 
              agent.patch_data?.data?.data
            ).length;
            
            console.log(`Poll ${pollCount}: ${agentsWithData}/${onlineAgents.length} agents have data`);
            
            if (agentsWithData > 0) {
              setError(`Loading patch data: ${agentsWithData}/${onlineAgents.length} agents completed...`);
            }
            
            if (agentsWithData === onlineAgents.length || pollCount >= maxPolls) {
              clearInterval(pollInterval);
              setFetchingPatchData(false);
              
              if (agentsWithData === onlineAgents.length) {
                setError(null);
                console.log('All agents have returned patch data');
              } else {
                setError(`Loaded data from ${agentsWithData}/${onlineAgents.length} agents. Some agents may be slow to respond.`);
              }
            }
          } catch (pollErr) {
            console.error('Error polling for updates:', pollErr);
          }
        }, 10000);
        
        setTimeout(() => {
          clearInterval(pollInterval);
          setFetchingPatchData(false);
        }, 120000);
        
      } catch (requestErr) {
        console.error('Failed to request patch info:', requestErr);
        setError(`Failed to request patch data: ${requestErr.message}`);
        setFetchingPatchData(false);
      }

    } catch (err) {
      console.error("Error initializing patch management:", err);
      setError(`Failed to load agents: ${err.message}`);
      setLoading(false);
      setFetchingPatchData(false);
    }
  };

  const loadScheduledJobs = async () => {
    try {
      const jobs = await patchService.getScheduledJobs();
      setScheduledJobs(jobs.scheduled_jobs || []);
    } catch (jobErr) {
      console.error('Failed to load scheduled jobs:', jobErr);
    }
  };

  const refreshAgentData = async () => {
    try {
      const response = await patchService.getPatchInfo();
      setAgents(response.agents || []);
      await loadScheduledJobs();
    } catch (err) {
      console.error("Error refreshing agents:", err);
    }
  };

  const handleManualRefresh = async () => {
    setError(null);
    await initializePatchManagement();
  };

  if (loading && agents.length === 0) {
    return (
      <div className="page-content">
        <Container fluid>
          <Card className="border-0 shadow-sm" style={{ backgroundColor: 'var(--bs-card-bg)' }}>
            <CardBody className="text-center py-5">
              <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
              <p className="mt-3 text-muted">Loading agents...</p>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* Page Header */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 fw-bold mb-1" style={{ color: 'var(--bs-body-color)' }}>
                <FeatherIcon icon="shield" size="24" className="me-2 text-primary" style={{ display: 'inline' }} />
                Patch Management
              </h1>
              <p className="text-muted mb-0">Deploy and schedule patches across your infrastructure</p>
            </div>
            <Button 
              color="outline-primary" 
              onClick={handleManualRefresh} 
              disabled={fetchingPatchData}
            >
              {fetchingPatchData ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <FeatherIcon icon="refresh-cw" size="14" className="me-1" style={{ display: 'inline' }} />
              )}
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <Alert color={fetchingPatchData ? "primary" : "warning"} className="mb-4">
            <FeatherIcon 
              icon={fetchingPatchData ? "loader" : "alert-triangle"} 
              size="16" 
              className="me-2" 
              style={{ display: 'inline' }} 
            />
            {error}
            {fetchingPatchData && <Spinner size="sm" className="ms-2" />}
          </Alert>
        )}

        {/* Agents Table */}
        <Card className="shadow-sm mb-4 border-0" style={{ backgroundColor: 'var(--bs-card-bg)' }}>
          <PatchAgentsTable agents={agents} />
        </Card>

        {/* Scheduled Patches Card */}
        <ScheduledPatchesCard
          agents={agents}
          scheduledJobs={scheduledJobs}
          refreshScheduledJobs={loadScheduledJobs}
        />
      </Container>
    </div>
  );
};

export default PatchManagement;